import React, { useEffect, useRef, useState } from 'react';
import './AddressAutocomplete.css';

// Track if script is currently loading
let scriptLoadingPromise = null;

// Load Google Maps script dynamically
const loadGoogleMapsScript = (apiKey) => {
  // Return existing promise if already loading
  if (scriptLoadingPromise) {
    return scriptLoadingPromise;
  }

  scriptLoadingPromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve();
      return;
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      // Wait for existing script to load
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
      } else {
        existingScript.addEventListener('load', () => {
          // Wait a bit for places library to be available
          setTimeout(() => resolve(), 100);
        });
        existingScript.addEventListener('error', reject);
      }
      return;
    }

    // Create new script - use callback method to avoid multiple loads
    window.initGoogleMaps = () => {
      delete window.initGoogleMaps;
      scriptLoadingPromise = null;
      resolve();
    };

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
    script.async = true;
    script.defer = true;
    script.id = 'google-maps-script';
    script.onerror = () => {
      delete window.initGoogleMaps;
      scriptLoadingPromise = null;
      reject(new Error('Google Maps script failed to load'));
    };

    document.head.appendChild(script);
  });

  return scriptLoadingPromise;
};

function AddressAutocomplete({ onAddressSelect, initialValue = '' }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [inputValue, setInputValue] = useState(initialValue);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // FIXED: Get API key from backend instead of frontend .env
    const fetchApiKey = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/google/api-key`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to get API key');
        }
        
        const data = await response.json();
        return data.apiKey;
      } catch (err) {
        console.error('Error fetching Google Maps API key:', err);
        // Fallback: try frontend env (for backward compatibility during transition)
        return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      }
    };

    fetchApiKey().then(apiKey => {
      if (!apiKey) {
        setError('Google Maps API key not configured');
        console.error('Missing Google Maps API key');
        return;
      }

      loadGoogleMapsScript(apiKey)
        .then(() => {
          setScriptLoaded(true);
          setError(null);
        })
        .catch((err) => {
          setError('Failed to load Google Maps');
          console.error('Google Maps loading error:', err);
        });
    });

    return () => {
      // Cleanup
      if (autocompleteRef.current && window.google) {
        try {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        } catch (e) {
          console.error('Cleanup error:', e);
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!scriptLoaded || !inputRef.current || !window.google) return;

    try {
      // Initialize autocomplete
      autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['address'],
        componentRestrictions: { country: 'us' }
      });

      // Listen for place selection
      const listener = autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current.getPlace();

        if (!place || !place.geometry) {
          console.error('No geometry found for place');
          return;
        }

        // Extract address components
        const components = {};
        if (place.address_components) {
          place.address_components.forEach(component => {
            const types = component.types;
            if (types.includes('street_number')) {
              components.streetNumber = component.long_name;
            }
            if (types.includes('route')) {
              components.route = component.long_name;
            }
            if (types.includes('locality')) {
              components.city = component.long_name;
            }
            if (types.includes('administrative_area_level_1')) {
              components.state = component.short_name;
            }
            if (types.includes('postal_code')) {
              components.zipCode = component.long_name;
            }
            if (types.includes('country')) {
              components.country = component.long_name;
            }
          });
        }

        // Build address
        const streetAddress = [
          components.streetNumber,
          components.route
        ].filter(Boolean).join(' ');

        const fullAddress = place.formatted_address || '';
        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();

        // Send data to parent
        if (onAddressSelect) {
          onAddressSelect({
            fullAddress: fullAddress,
            streetAddress: streetAddress,
            city: components.city || '',
            state: components.state || '',
            zipCode: components.zipCode || '',
            country: components.country || 'USA',
            latitude: latitude,
            longitude: longitude
          });
        }

        setInputValue(fullAddress);
      });

      // Cleanup listener on unmount
      return () => {
        if (listener && window.google) {
          window.google.maps.event.removeListener(listener);
        }
      };
    } catch (err) {
      setError('Failed to initialize autocomplete');
      console.error('Autocomplete initialization error:', err);
    }
  }, [scriptLoaded, onAddressSelect]);

  return (
    <div>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder="Start typing your address..."
        disabled={!scriptLoaded && !error}
        className={`address-input${error ? ' address-input--error' : ''}`}
      />
      {!scriptLoaded && !error && (
        <p className="address-hint">
          Loading address autocomplete...
        </p>
      )}
      {error && (
        <p className="address-error-msg">
          {error} - You can still type your address manually
        </p>
      )}
    </div>
  );
}

export default AddressAutocomplete;
